import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
  Divider,
} from '@mui/material';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';

const StatCard = ({ label, value, icon, caption }) => (
  <Card sx={{ borderRadius: '12px', height: '100%' }}>
    <CardContent>
      <Stack direction="row" spacing={2} alignItems="center">
        {icon && (
          <Box
            sx={{
              width: 48,
              height: 48,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 2,
              bgcolor: 'rgba(122,59,193,0.12)',
              color: 'primary.main',
            }}
          >
            {icon}
          </Box>
        )}
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
            {label}
          </Typography>
          <Typography variant="h5" fontWeight={600} sx={{ mt: 0.5 }}>
            {value}
          </Typography>
          {caption && (
            <Typography variant="body2" color="text.secondary">
              {caption}
            </Typography>
          )}
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

const EventListCard = ({ events, onSelect }) => (
  <Card sx={{ borderRadius: '12px' }}>
    <CardContent>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6">Recent fall events</Typography>
        <Button size="small" variant="text" onClick={() => onSelect && onSelect(events[0])} disabled={!events.length}>
          View most recent
        </Button>
      </Stack>
      {!events.length ? (
        <Typography variant="body2" color="text.secondary">
          There are no recorded falls for your profile yet. You'll see new incidents here as they are detected.
        </Typography>
      ) : (
        <List dense disablePadding>
          {events.map((event, idx) => (
            <React.Fragment key={event.id || idx}>
              <ListItem
                alignItems="flex-start"
                sx={{ px: 0, py: 1.25 }}
                secondaryAction={
                  <Button
                    size="small"
                    startIcon={<PlayCircleOutlineIcon fontSize="small" />}
                    onClick={() => onSelect(event)}
                  >
                    Open
                  </Button>
                }
              >
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography fontWeight={600}>
                        {event.camera?.name || event.camera_name || 'Unknown camera'}
                      </Typography>
                      {event.status && (
                        <Typography variant="caption" color="text.secondary">
                          {event.status.replaceAll('_', ' ').toLowerCase()}
                        </Typography>
                      )}
                    </Stack>
                  }
                  secondary={
                    <Typography variant="body2" color="text.secondary">
                      {new Date(event.timestamp || event.detected_at || Date.now()).toLocaleString()}
                    </Typography>
                  }
                />
              </ListItem>
              {idx < events.length - 1 && <Divider component="li" />}
            </React.Fragment>
          ))}
        </List>
      )}
    </CardContent>
  </Card>
);

const ClientOverviewTab = ({
  stats,
  recentEvents,
  onSelectEvent,
  onBrowseHistory,
  onContactSupport,
  footerCaption,
}) => (
  <>
    <Grid container spacing={3}>
      {stats.map((stat) => (
        <Grid item xs={12} md={4} key={stat.label}>
          <StatCard {...stat} />
        </Grid>
      ))}
    </Grid>

    <Grid container spacing={3} alignItems="stretch" sx={{ mt: 0 }}>
      <Grid item xs={12} md={6}>
        <EventListCard events={recentEvents} onSelect={onSelectEvent} />
      </Grid>
      <Grid item xs={12} md={6}>
        <Card sx={{ borderRadius: '12px', height: '100%' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Next steps & resources
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              You can review incidents, leave notes for responders, or notify the support team when additional
              follow-up is required.
            </Typography>
            <Stack spacing={1.5} sx={{ flexWrap: 'wrap' }}>
              <Button variant="contained" onClick={onBrowseHistory}>
                Browse full fall history
              </Button>
              <Button variant="outlined" onClick={onContactSupport}>
                Contact support team
              </Button>
              <Typography variant="caption" color="text.secondary">
                {footerCaption}
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  </>
);

export default ClientOverviewTab;
